<template>
  <div class="mini-tab-content">
    <div
      v-if="loadingRegulations"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="primary"
      />
      <span>{{ $t('involvedPanel.regulation.messages.loading') }}</span>
    </div>
    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="FileTextIcon"
            size="13"
            class="mr-25 text-primary"
          />
          {{ toolbarTitle }}
        </span>
        <button
          v-if="!readonly"
          class="btn btn-sm btn-primary"
          @click="showRegulationModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.regulation.buttons.submit') }}
        </button>
      </div>

      <div
        v-if="!regulations.length"
        class="interc-empty"
      >
        <feather-icon
          icon="FileTextIcon"
          size="26"
        />
        <p>{{ $t('involvedPanel.regulation.empty.message') }}</p>
      </div>

      <div
        v-if="regulations.length"
        class="interc-list"
      >
        <div
          v-for="mr in regulations"
          :key="mr.id"
          :class="['interc-item', { 'interc-item--cancelled': mr.cancelled_by_user_id }]"
        >
          <div class="interc-item__top">
            <div class="interc-item__badge">
              <feather-icon
                :icon="mr.cancelled_by_user_id ? 'XCircleIcon' : 'FileTextIcon'"
                size="11"
                class="mr-25"
              />
              {{ $t('involvedPanel.regulation.item.badge') }}
            </div>
            <button
              v-if="!readonly && !mr.cancelled_by_user_id"
              class="interc-item__del"
              :title="$t('involvedPanel.regulation.item.deleteTooltip')"
              @click="deleteRegulation(mr.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              />
            </button>
          </div>
          <div
            class="evolution-content"
            v-html="mr.description"
          />
          <div class="interc-item__meta">
            <feather-icon
              icon="UserIcon"
              size="11"
            />
            <span>{{ mr.user ? mr.user.name : '—' }}</span>
            <span class="interc-item__dot" />
            <feather-icon
              icon="ClockIcon"
              size="11"
            />
            <span>{{ mr.created_at | formatDate }}</span>
          </div>
          <div
            v-if="mr.cancelled_by_user_id"
            class="interc-item__cancelled-row"
          >
            <feather-icon
              icon="XCircleIcon"
              size="11"
              class="mr-25"
            />
            {{ $t('involvedPanel.regulation.item.cancelledBy') }}
            <strong>{{ mr.cancelled_by ? mr.cancelled_by.name : '—' }}</strong>
            · {{ mr.cancelled_at | formatDate }}
          </div>
        </div>
      </div>
    </template>

    <b-modal
      v-model="showRegulationModal"
      :title="$t('involvedPanel.regulation.modal.title')"
      :title-class="'font-weight-bold'"
      size="lg"
      centered
      lazy
      no-enforce-focus
      no-close-on-backdrop
      @hidden="newRegulation = { description: '' }"
    >
      <div class="form-group mb-1">
        <label class="interc-label"><span class="required">*</span> {{ $t('involvedPanel.regulation.modal.label') }}</label>
        <ckeditor
          :value="newRegulation.description"
          :editor="editor"
          :config="editorConfig"
          @input="newRegulation.description = $event"
        />
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showRegulationModal = false"
        >
          {{ $t('involvedPanel.regulation.buttons.cancel') }}
        </button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="savingRegulation || !regulationHasContent"
          @click="saveRegulation"
        >
          <b-spinner
            v-if="savingRegulation"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="FileTextIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.regulation.buttons.submit') }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue2'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'RegulationPanel',
  components: { ckeditor: CKEditor.component },
  mixins: [involvedPanelMixin],
  data() {
    return {
      regulations: [],
      loadingRegulations: false,
      showRegulationModal: false,
      savingRegulation: false,
      newRegulation: { description: '' },
      editorConfig: {
        toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', '|', 'undo', 'redo'],
      },
    }
  },
  computed: {
    regulationHasContent() {
      // Remove tags HTML para não habilitar o botão com editor "vazio" (<p></p>, &nbsp;).
      const text = (this.newRegulation.description || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim()
      return text.length > 0
    },
    toolbarTitle() {
      return this.regulations.length
        ? `${this.regulations.length} ${this.$tc('involvedPanel.regulation.toolbar.count', this.regulations.length)}`
        : this.$t('involvedPanel.regulation.toolbar.noRegulations')
    },
  },
  // Mantém o build do CKEditor FORA da reatividade do Vue (boa prática oficial):
  // se ClassicEditor for observado pelo Vue, a instância pode quebrar e parar de emitir input.
  created() {
    this.editor = ClassicEditor
  },
  mounted() {
    this.loadRegulations()
  },
  methods: {
    // Chamado pelo mixin ao reativar a aba (keep-alive): evita dados velhos sem F5.
    refresh() {
      return this.loadRegulations()
    },

    async loadRegulations() {
      this.loadingRegulations = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_regulations`, {
          params: {
            occurrence_id: Number(this.occurrenceId),
            involved_id: this.involvedId ? Number(this.involvedId) : undefined,
          },
        })
        this.regulations = res.data.data || []
      } catch (e) {
        this.regulations = []
        this.$message(
          this.$t('involvedPanel.regulation.messages.loadingError.title'),
          this.$t('involvedPanel.regulation.messages.loadingError.body'),
          'error',
        )
      } finally {
        this.loadingRegulations = false
      }
    },
    async saveRegulation() {
      if (!this.regulationHasContent) return
      this.savingRegulation = true
      try {
        const { user } = this.$store.state
        await this.$http.post(`${this.$store.state.api}occurrence_regulations`, {
          occurrence_id: Number(this.occurrenceId),
          involved_id: this.involvedId ? Number(this.involvedId) : null,
          user_id: user.id,
          description: this.newRegulation.description,
        })
        // A resposta do POST não traz o autor (relação `user`); recarrega a lista.
        await this.refresh()
        this.newRegulation = { description: '' }
        this.showRegulationModal = false
        this.$message(
          this.$t('involvedPanel.regulation.messages.saveSuccess.title'),
          this.$t('involvedPanel.regulation.messages.saveSuccess.body'),
          'success',
        )
      } catch (e) {
        this.$message(
          this.$t('involvedPanel.regulation.messages.saveError.title'),
          this.$t('involvedPanel.regulation.messages.saveError.body'),
          'error',
        )
      } finally {
        this.savingRegulation = false
      }
    },
    async deleteRegulation(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.regulation.messages.deleteConfirm.body'),
        {
          title: this.$t('involvedPanel.regulation.messages.deleteConfirm.title'),
          okTitle: this.$t('involvedPanel.regulation.buttons.deleteConfirm'),
          cancelTitle: this.$t('involvedPanel.regulation.buttons.cancel'),
          okVariant: 'danger',
          centered: true,
          size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_regulations/${id}`)
        const updated = res.data.data || res.data
        const idx = this.regulations.findIndex(mr => mr.id === id)
        if (idx !== -1) this.$set(this.regulations, idx, updated)
      } catch (e) {
        this.$message(
          this.$t('involvedPanel.regulation.messages.deleteError.title'),
          this.$t('involvedPanel.regulation.messages.deleteError.body'),
          'error',
        )
      }
    },
  },
}
</script>
